import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Card,
  CardHeader,
  CardContent,
  CardMedia,
  CardActions,
  IconButton,
  Button,
  Avatar,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Favorite as FavoriteIcon,
  Comment as CommentIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { tokens } from "../components/Theme";
import { CommentNews } from "../api/News/CommentNews";
import { createNewsWithImage } from "../api/News/CreateNews";
import { DeleteNews } from "../api/News/DeleteNews";
import { LikeNews } from "../api/News/LikeNews";
import { UnLikeNews } from "../api/News/UnlineNews";
import { UpdateNews } from "../api/News/UpdateNews";

import axios from "axios";

function News() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userData = JSON.parse(localStorage.getItem("userdata")) || {};
  const defaultRole = userData.level === 1 ? "admin" : "user";
  const [role] = useState(defaultRole);
  const [data, setData] = useState([]);
  const [images, setImages] = useState({});
  const [newComments, setNewComments] = useState({});

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // "success" | "error"
  });

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const fetchNews = async () => {
      const token = JSON.parse(localStorage.getItem("authentication"));
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_API}/api/news/GetNews`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const news = await res.json();
      setData(news);

      const imgs = {};
      for (const n of news) {
        if (n.imageUrl) {
          imgs[n.appNewsId] = `${n.imageUrl}`;
        }
      }
      setImages(imgs);
    };

    fetchNews();
  }, []);

  const isNewsNew = (newsItem) => newsItem.isNew === true;

  const handleAdd = () => {
    const newId = `NKS${Date.now()}`;
    const newNews = {
      appNewsId: newId,
      province: "",
      announceDate: new Date().toISOString().slice(0, 10),
      newsGroup: "",
      newsTopic: "",
      newsDetail: "",
      noOfLike: 0,
      noOfComments: 0,
      comments: [],
      isNew: true,
    };
    setData((prev) => [newNews, ...prev]);
  };
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    indexToDelete: null,
  });
  const confirmDelete = (index) => {
    setDeleteConfirm({ open: true, indexToDelete: index });
  };
  const handleSave = async (index) => {
    const newsItem = data[index];
    const file = images[newsItem.appNewsId];
    const isNew = isNewsNew(newsItem);

    const payload = {
      ...newsItem,
      imageUrl: "",
    };
    delete payload.isNew;

    try {
      if (isNew) {
        const res = await createNewsWithImage(payload, file);
        if (res.status !== 200) throw new Error(res.message);
      } else {
        if (file instanceof File) {
          const token = JSON.parse(localStorage.getItem("authentication"));
          const formData = new FormData();
          formData.append("file", file);
          formData.append("newsId", newsItem.appNewsId);

          const uploadRes = await axios.post(
            `${process.env.REACT_APP_BACKEND_API}/api/news/img`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "multipart/form-data",
              },
            }
          );
          const fileName = uploadRes.data.name;
          payload.imageUrl = `${process.env.REACT_APP_STORAGE_URL}${fileName}`;
        }

        const updateRes = await UpdateNews(payload);
        if (updateRes.status !== 200) throw new Error("Update failed");
      }

      showSnackbar("บันทึกสำเร็จ", "success");
    } catch (e) {
      console.error("Save news failed:", e);
      showSnackbar("เกิดข้อผิดพลาดในการบันทึก", "error");
    }
  };

  const handleImageUpload = (id, file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setImages((prev) => ({ ...prev, [id]: file }));
    };
    reader.readAsDataURL(file);
  };

  const handleLike = async (news) => {
    const id = news.appNewsId;
    const farmerId = userData.appFarmerId;

    if (!farmerId) return;

    const hasLiked = news.like?.includes(farmerId);

    try {
      if (hasLiked) {
        await UnLikeNews(id, farmerId);
        news.like = news.like.filter((fid) => fid !== farmerId);
        news.noOfLike -= 1;
      } else {
        const farmerId = userData?.appFarmerId;
        if (!farmerId) return;
        await LikeNews(id, farmerId);
        news.like.push(farmerId);
        news.noOfLike += 1;
      }

      setData([...data]);
    } catch (error) {
      console.error("Error liking/unliking news:", error);
    }
  };
  const handleAddComment = async (index) => {
    const news = data[index];
    const comment = newComments[news.appNewsId]?.trim();
    if (!comment) return;
    const userName = userData.name || "ผู้ใช้ทั่วไป";

    await CommentNews(news.appNewsId, comment, userName);

    const newComment = {
      name: userName,
      text: comment,
    };
    news.comments.push(newComment);
    news.noOfComments++;
    setNewComments((prev) => ({ ...prev, [news.appNewsId]: "" }));
    setData([...data]);
  };

  const handleDelete = async () => {
    const index = deleteConfirm.indexToDelete;
    if (index === null) return;

    try {
      const id = data[index].appNewsId;
      await DeleteNews(id);
      setData(data.filter((_, i) => i !== index));
      showSnackbar("ลบข่าวเรียบร้อย", "success");
    } catch (error) {
      console.error("Error deleting news:", error);
      showSnackbar("เกิดข้อผิดพลาดในการลบข่าว", "error");
    }
    setDeleteConfirm({ open: false, indexToDelete: null });
  };

  const handleChange = (index, field, value) => {
    const updated = [...data];
    updated[index][field] = value;
    setData(updated);
  };

  return (
    <Box p={2}>
      <Typography variant="h5" mb={2}>
        ฟีดข่าวสาร
      </Typography>

      {data.map((row, index) => (
        <Card key={row.appNewsId} sx={{ mb: 2 }}>
          <CardHeader
            avatar={<Avatar>{row.province?.[0] || "?"}</Avatar>}
            title={row.newsTopic || "ไม่มีหัวข้อ"}
            subheader={`📍 ${row.province} | 📅 ${row.announceDate}`}
          />
          {images[row.appNewsId] && (
            <CardMedia
              component="img"
              height={isMobile ? undefined : "500px"}
              src={
                images[row.appNewsId] instanceof File
                  ? URL.createObjectURL(images[row.appNewsId])
                  : typeof images[row.appNewsId] === "string"
                    ? images[row.appNewsId]
                    : undefined
              }
              alt="news"
              sx={{ objectFit: "cover" }}
              onError={(e) => {
                console.error("📛 รูปโหลดไม่ได้:", e.target.src);
                e.target.style.display = "none";
              }}
            />
          )}
          <CardContent>
            <Typography variant="body2" color="text.secondary">
              {row.newsDetail}
            </Typography>

            {role === "admin" && (
              <Box mt={2}>
                <TextField
                  label="จังหวัด"
                  fullWidth
                  size="small"
                  value={row.province}
                  onChange={(e) =>
                    handleChange(index, "province", e.target.value)
                  }
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="กลุ่มข่าว"
                  fullWidth
                  size="small"
                  value={row.newsGroup}
                  onChange={(e) =>
                    handleChange(index, "newsGroup", e.target.value)
                  }
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="หัวข้อข่าว"
                  fullWidth
                  size="small"
                  value={row.newsTopic}
                  onChange={(e) =>
                    handleChange(index, "newsTopic", e.target.value)
                  }
                  sx={{ mb: 1 }}
                />
                <TextField
                  label="รายละเอียดข่าว"
                  fullWidth
                  multiline
                  size="small"
                  value={row.newsDetail}
                  onChange={(e) =>
                    handleChange(index, "newsDetail", e.target.value)
                  }
                  sx={{ mb: 1 }}
                />

                <Button component="label" variant="outlined" size="small">
                  เลือกรูป
                  <input
                    hidden
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      handleImageUpload(row.appNewsId, e.target.files[0])
                    }
                  />
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  sx={{ ml: 1 }}
                  onClick={() => handleSave(index)}
                >
                  <SaveIcon fontSize="small" /> บันทึก
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  onClick={() => confirmDelete(index)}
                  sx={{ ml: 1 }}
                >
                  <DeleteIcon fontSize="small" /> ลบ
                </Button>
              </Box>
            )}
          </CardContent>

          <CardActions disableSpacing>
            <IconButton onClick={() => handleLike(row)}>
              <FavoriteIcon
                color={
                  row.like?.includes(userData.appFarmerId)
                    ? "error"
                    : "disabled"
                }
              />
            </IconButton>
            <Typography variant="caption">{row.noOfLike} ไลก์</Typography>

            <IconButton>
              <CommentIcon />
            </IconButton>
            <Typography variant="caption">
              {row.noOfComments} ความคิดเห็น
            </Typography>
          </CardActions>

          <CardContent sx={{ pt: 0 }}>
            {row.comments?.map((comment, idx) => (
              <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                <strong>{comment.name}:</strong> {comment.text}
              </Typography>
            ))}
            <Box display="flex" alignItems="center" mt={1}>
              <TextField
                fullWidth
                placeholder="แสดงความคิดเห็น..."
                size="small"
                value={newComments[row.appNewsId] || ""}
                onChange={(e) =>
                  setNewComments((prev) => ({
                    ...prev,
                    [row.appNewsId]: e.target.value,
                  }))
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => handleAddComment(index)}>
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </CardContent>
        </Card>
      ))}

      {role === "admin" && (
        <Box mt={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            color="success"
          >
            เพิ่มข่าว
          </Button>
        </Box>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Dialog
        open={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, indexToDelete: null })}
      >
        <DialogTitle>ยืนยันการลบข่าว</DialogTitle>
        <DialogContent>
          <DialogContentText>
            คุณแน่ใจที่จะลบข่าวนี้? การลบจะไม่สามารถย้อนกลับได้
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setDeleteConfirm({ open: false, indexToDelete: null })
            }
            color="primary"
          >
            ยกเลิก
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            ลบ
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default News;
